// INT tops out at Elite 80% (owner, 12 Oct 2026); LEGEND is a UAE-only status set by staff (owner, 13 Oct 2026).
export type ProgressionTier = 'STARTER' | 'ACTIVE' | 'RISING' | 'PRO' | 'SENIOR' | 'ELITE';

export type AgentTierName = ProgressionTier | 'REFERRAL';

export const TIER_ORDER: ProgressionTier[] = ['STARTER', 'ACTIVE', 'RISING', 'PRO', 'SENIOR', 'ELITE'];

// Not on the INT ladder and never used for pay; UAE profiles only.
export const UAE_LEGEND_TIER = 'LEGEND' as const;
export type DisplayTierName = AgentTierName | typeof UAE_LEGEND_TIER;

// Tiers staff can set on a profile; Legend only for UAE agents.
export const ASSIGNABLE_TIERS: readonly AgentTierName[] = ['REFERRAL', ...TIER_ORDER];
export const assignableTiersForMarket = (market: string | null | undefined): readonly DisplayTierName[] =>
  market === 'UAE' ? [...ASSIGNABLE_TIERS, UAE_LEGEND_TIER] : ASSIGNABLE_TIERS;
export const isTierAssignableForMarket = (tier: string, market: string | null | undefined): boolean =>
  (assignableTiersForMarket(market) as readonly string[]).includes(tier);

export const PROGRESSION_TIER_RANGE_AED: Record<ProgressionTier, { min: number; maxInclusive: number | null }> = {
  STARTER: { min: 0, maxInclusive: 99_999 },
  ACTIVE: { min: 100_000, maxInclusive: 299_999 },
  RISING: { min: 300_000, maxInclusive: 599_999 },
  PRO: { min: 600_000, maxInclusive: 999_999 },
  SENIOR: { min: 1_000_000, maxInclusive: 1_999_999 },
  ELITE: { min: 2_000_000, maxInclusive: null },
};

export const TIER_THRESHOLDS: Record<ProgressionTier, number> = {
  STARTER: 0,
  ACTIVE: 100_000,
  RISING: 300_000,
  PRO: 600_000,
  SENIOR: 1_000_000,
  ELITE: 2_000_000,
};

export const AGENT_TIER_COMMISSION_PERCENT: Record<AgentTierName, number> = {
  REFERRAL: 30,
  STARTER: 50,
  ACTIVE: 60,
  RISING: 65,
  PRO: 70,
  SENIOR: 75,
  ELITE: 80,
};

// Code mirror of the top tier's self-close split — only for when the live tier config can't be read.
export const FALLBACK_TOP_SELF_CLOSE_PERCENT = Math.max(
  ...TIER_ORDER.map((tier) => AGENT_TIER_COMMISSION_PERCENT[tier]),
);

export const AGENT_TIER_REFERRAL_PERCENT: Record<ProgressionTier, number> = {
  STARTER: 30,
  ACTIVE: 35,
  RISING: 40,
  PRO: 45,
  SENIOR: 50,
  ELITE: 55,
};

// #50 — AM commission shares taken out of agents' deal commissions.
// Percentages are fractions of the closing agent's commission that the
// Account Manager receives. The relationship between the AM and the agent
// determines which row applies:
//   - MANAGE_ONLY: AM inherited the agent from the roster, did not recruit.
//   - RECRUIT_AND_MANAGE: AM brought the agent in AND manages them.
//   - PURE_REFERRER: AM only referred the agent, another AM manages them.
// Stored on AgentProfile.referralPayoutPercent — this table is the canonical
// source of truth the team uses to set that column on each agent profile.
export type AmRelationship = 'MANAGE_ONLY' | 'RECRUIT_AND_MANAGE' | 'PURE_REFERRER';

export const AM_COMMISSION_SHARES = {
  MANAGE_ONLY: { default: 6, min: 5, max: 7 },
  RECRUIT_AND_MANAGE: { exact: 25 },
  PURE_REFERRER_TIER_MAP: {
    STARTER: 18,
    ACTIVE: 18,
    RISING: 19,
    PRO: 19,
    SENIOR: 20,
    ELITE: 20,
  },
} as const;

export function resolveAmCommissionSharePercent(
  relationship: AmRelationship,
  agentTier: ProgressionTier = 'STARTER',
): number {
  switch (relationship) {
    case 'RECRUIT_AND_MANAGE':
      return AM_COMMISSION_SHARES.RECRUIT_AND_MANAGE.exact;
    case 'MANAGE_ONLY':
      return AM_COMMISSION_SHARES.MANAGE_ONLY.default;
    case 'PURE_REFERRER':
      return (
        AM_COMMISSION_SHARES.PURE_REFERRER_TIER_MAP[agentTier] ?? AM_COMMISSION_SHARES.PURE_REFERRER_TIER_MAP.STARTER
      );
  }
}

/** English tier names — staff-only (admin/AM) screens; portal UI uses AGENT_TIER_LABEL_KEYS via useAgentTierLabel(). */
const LABELS: Record<string, string> = {
  REFERRAL: 'Referral',
  STARTER: 'Starter',
  ACTIVE: 'Active',
  RISING: 'Rising',
  PRO: 'Pro',
  SENIOR: 'Senior',
  ELITE: 'Elite',
  LEGEND: 'Legend',
};

/** i18n keys for tier display names (namespace `rankings`). Tier ids stay English in data/API. */
export const AGENT_TIER_LABEL_KEYS: Record<DisplayTierName, string> = {
  REFERRAL: 'rankings.tiers.referral',
  STARTER: 'rankings.tiers.starter',
  ACTIVE: 'rankings.tiers.active',
  RISING: 'rankings.tiers.rising',
  PRO: 'rankings.tiers.pro',
  SENIOR: 'rankings.tiers.senior',
  ELITE: 'rankings.tiers.elite',
  LEGEND: 'rankings.tiers.legend',
};

/** i18n keys for the one-line tier profile descriptions. */
export const AGENT_TIER_PROFILE_LINE_KEYS: Record<AgentTierName, string> = {
  REFERRAL: 'rankings.tiers.profileReferral',
  STARTER: 'rankings.tiers.profileStarter',
  ACTIVE: 'rankings.tiers.profileActive',
  RISING: 'rankings.tiers.profileRising',
  PRO: 'rankings.tiers.profilePro',
  SENIOR: 'rankings.tiers.profileSenior',
  ELITE: 'rankings.tiers.profileElite',
};

/** i18n keys for the tier-table tooltips (Self-close / Referral / tier progress). */
export const AGENT_TIER_TABLE_TOOLTIP_KEYS = {
  selfClose: 'rankings.tiers.tooltipSelfClose',
  referral: 'rankings.tiers.tooltipReferral',
  tierProgress: 'rankings.tiers.tooltipTierProgress',
} as const;

export const AGENT_TIER_STARTING_TIER_NOTE_KEY = 'rankings.tiers.startingTierNote';

export type AgentTierIconTheme = { fill: string; star: string; label: string };

const AGENT_TIER_ICON_THEME: Record<DisplayTierName, AgentTierIconTheme> = {
  REFERRAL: { fill: '#9CA3AF', star: '#F9FAFB', label: '#4B5563' },
  STARTER: { fill: '#A67C52', star: '#FFF8F0', label: '#5C4033' },
  ACTIVE: { fill: '#8B95A5', star: '#F4F6F8', label: '#3D4654' },
  RISING: { fill: '#C9A227', star: '#FFFDF5', label: '#7A5F0B' },
  PRO: { fill: '#2D8A72', star: '#ECFDF5', label: '#14534A' },
  SENIOR: { fill: '#2B6FD6', star: '#F0F7FF', label: '#154FA6' },
  ELITE: { fill: '#6D28D9', star: '#F5F3FF', label: '#4C1D95' },
  LEGEND: { fill: '#D4A032', star: '#FFF8E6', label: '#0D0F15' },
};

export const getAgentTierIconTheme = (tier: string): AgentTierIconTheme =>
  AGENT_TIER_ICON_THEME[tier as DisplayTierName] ?? {
    fill: '#6B7280',
    star: '#F9FAFB',
    label: '#374151',
  };

export const formatAgentTierLabel = (tier: string) => (tier ? (LABELS[tier] ?? tier) : '');

export function getCommissionPercentForTier(tier: string): number {
  return AGENT_TIER_COMMISSION_PERCENT[tier as AgentTierName] ?? AGENT_TIER_COMMISSION_PERCENT.STARTER;
}

/** Referral commission % — used for service referrals (not self-closed deals). */
export function getReferralCommissionPercentForTier(tier: string): number {
  return AGENT_TIER_REFERRAL_PERCENT[tier as ProgressionTier] ?? AGENT_TIER_REFERRAL_PERCENT.STARTER;
}
